import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { branchFinanceApi } from '../../services/branchFinanceApi';
import type {
    BranchOption,
    CodReceipt,
    CodReceiptListResponse,
    DeliverySheetConsignment,
    DeliverySheetOption,
    RiderOption,
} from '../../pages/BranchFinances/types/branchFinance.types';

interface CodReceiptFilters {
    branchId?: number;
    riderId?: number;
}

interface BranchCodReceiptsState {
    list: CodReceipt[];
    loadingList: boolean;
    error: string | null;
    branches: BranchOption[];
    filters: CodReceiptFilters;
    pagination: CodReceiptListResponse['meta'] | null;
    riders: RiderOption[];
    ridersLoading: boolean;
    deliverySheets: DeliverySheetOption[];
    deliverySheetsLoading: boolean;
    consignments: DeliverySheetConsignment[];
    consignmentsLoading: boolean;
    submitting: boolean;
    formError: string | null;
}

const initialState: BranchCodReceiptsState = {
    list: [],
    loadingList: false,
    error: null,
    branches: [],
    filters: {},
    pagination: null,
    riders: [],
    ridersLoading: false,
    deliverySheets: [],
    deliverySheetsLoading: false,
    consignments: [],
    consignmentsLoading: false,
    submitting: false,
    formError: null,
};

export const fetchBranchCodReceipts = createAsyncThunk(
    'branchCodReceipts/fetchList',
    async (params: { branchId?: number; riderId?: number; page?: number; perPage?: number } = {}) => {
        return await branchFinanceApi.getCodReceipts(params);
    }
);

export const createBranchCodReceipt = createAsyncThunk(
    'branchCodReceipts/create',
    async (payload: { riderId: number; deliverySheetId: number; consignmentIds: number[] }) => {
        return await branchFinanceApi.createCodReceipt({
            rider_id: payload.riderId,
            deliverysheet_id: payload.deliverySheetId,
            consignment_ids: payload.consignmentIds,
        });
    }
);

export const fetchCodRiders = createAsyncThunk(
    'branchCodReceipts/fetchRiders',
    async (params: { branchId?: number } = {}) => {
        return await branchFinanceApi.getCodRiders(params);
    }
);

export const fetchCodDeliverySheets = createAsyncThunk(
    'branchCodReceipts/fetchDeliverySheets',
    async (riderId: number) => {
        return await branchFinanceApi.getCodDeliverySheets(riderId);
    }
);

export const fetchCodConsignments = createAsyncThunk(
    'branchCodReceipts/fetchConsignments',
    async (deliverySheetId: number) => {
        return await branchFinanceApi.getCodConsignments(deliverySheetId);
    }
);

const branchCodReceiptsSlice = createSlice({
    name: 'branchCodReceipts',
    initialState,
    reducers: {
        setCodReceiptFilters: (state, action: PayloadAction<CodReceiptFilters>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearCodReceiptError: (state) => {
            state.error = null;
            state.formError = null;
        },
        resetDeliverySheetData: (state) => {
            state.deliverySheets = [];
            state.consignments = [];
        },
        resetConsignments: (state) => {
            state.consignments = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBranchCodReceipts.pending, (state) => {
                state.loadingList = true;
                state.error = null;
            })
            .addCase(
                fetchBranchCodReceipts.fulfilled,
                (state, action: PayloadAction<CodReceiptListResponse>) => {
                    state.loadingList = false;
                    state.list = action.payload.data;
                    state.pagination = action.payload.meta;
                    if (action.payload.filters?.branches) {
                        state.branches = action.payload.filters.branches;
                    }
                }
            )
            .addCase(fetchBranchCodReceipts.rejected, (state, action) => {
                state.loadingList = false;
                state.error = action.error.message || 'Failed to load COD receipts';
            });

        builder
            .addCase(createBranchCodReceipt.pending, (state) => {
                state.submitting = true;
                state.formError = null;
            })
            .addCase(createBranchCodReceipt.fulfilled, (state, action: PayloadAction<CodReceipt>) => {
                state.submitting = false;
                state.list = [action.payload, ...state.list];
                if (state.pagination) {
                    state.pagination.total += 1;
                    const perPage = state.pagination.per_page;
                    if (perPage && state.list.length > perPage) {
                        state.list = state.list.slice(0, perPage);
                    }
                }
            })
            .addCase(createBranchCodReceipt.rejected, (state, action) => {
                state.submitting = false;
                state.formError = action.error.message || 'Failed to create COD receipt';
            });

        builder
            .addCase(fetchCodRiders.pending, (state) => {
                state.ridersLoading = true;
            })
            .addCase(fetchCodRiders.fulfilled, (state, action: PayloadAction<RiderOption[]>) => {
                state.ridersLoading = false;
                state.riders = action.payload;
            })
            .addCase(fetchCodRiders.rejected, (state) => {
                state.ridersLoading = false;
            });

        builder
            .addCase(fetchCodDeliverySheets.pending, (state) => {
                state.deliverySheetsLoading = true;
            })
            .addCase(
                fetchCodDeliverySheets.fulfilled,
                (state, action: PayloadAction<DeliverySheetOption[]>) => {
                    state.deliverySheetsLoading = false;
                    state.deliverySheets = action.payload;
                }
            )
            .addCase(fetchCodDeliverySheets.rejected, (state) => {
                state.deliverySheetsLoading = false;
            });

        builder
            .addCase(fetchCodConsignments.pending, (state) => {
                state.consignmentsLoading = true;
            })
            .addCase(
                fetchCodConsignments.fulfilled,
                (state, action: PayloadAction<DeliverySheetConsignment[]>) => {
                    state.consignmentsLoading = false;
                    state.consignments = action.payload;
                }
            )
            .addCase(fetchCodConsignments.rejected, (state) => {
                state.consignmentsLoading = false;
            });
    },
});

export const {
    setCodReceiptFilters,
    clearCodReceiptError,
    resetDeliverySheetData,
    resetConsignments,
} = branchCodReceiptsSlice.actions;

export default branchCodReceiptsSlice.reducer;


